import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { FaArrowsAltH } from "react-icons/fa";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

export default function CustomDataTable({
  columns = [],
  data = [],
  tableKey,
  sellerId,
  extraColumn = null,
  pagination = true,
  forceFixedLayout = false,
}) {
  const MIN_COLUMN_WIDTH = 120;
  const DEFAULT_COLUMN_WIDTH = 200;

  const [tableState, setTableState] = useState({
    columnWidths: [],
    columnOrder: [],
    hoveredColumn: null,
    isLoading: false,
    draggedColumn: null,
    dragOverColumn: null,
  });

  const [sortState, setSortState] = useState({
    columnIndex: null,
    direction: "asc",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize column state when columns change
  useEffect(() => {
    if (columns.length > 0) {
      setTableState((prev) => {
        const allColumns = extraColumn ? [...columns, extraColumn] : columns;
        const totalLength = allColumns.length;

        // Initialize widths with proper fallbacks
        const newColumnWidths = allColumns.map((col, index) => {
          const existingWidth = prev.columnWidths?.[index];
          const configuredWidth = col?.width;
          const candidate =
            existingWidth ?? configuredWidth ?? DEFAULT_COLUMN_WIDTH;
          return Math.max(Number(candidate), MIN_COLUMN_WIDTH);
        });

        // Initialize column order
        const validColumnOrder = (prev.columnOrder || []).filter(
          (index) => index >= 0 && index < totalLength
        );

        const missingIndices = [];
        for (let i = 0; i < totalLength; i++) {
          if (!validColumnOrder.includes(i)) {
            missingIndices.push(i);
          }
        }

        const finalColumnOrder =
          validColumnOrder.length > 0
            ? [...validColumnOrder, ...missingIndices]
            : Array.from({ length: totalLength }, (_, i) => i);

        return {
          ...prev,
          columnOrder: finalColumnOrder,
          columnWidths: newColumnWidths,
        };
      });
    }
  }, [columns, extraColumn]);

  const baseurl = process.env.REACT_APP_BASE_URL;

  const tableRef = useRef(null);
  const debounceTimeout = useRef(null);
  const initialLoadComplete = useRef(false);

  const saveColumnWidths = useCallback(
    async (widths) => {
      try {
        await axios.post(
          `${baseurl}columnWidth/save-column-widths/${sellerId}/${tableKey}`,
          { columnWidths: widths }
        );
      } catch (error) {
        console.error("Error saving column widths:", error);
      }
    },
    [baseurl, sellerId, tableKey]
  );

  const saveColumnOrder = useCallback(
    async (order) => {
      try {
        await axios.post(
          `${baseurl}columnArrangement/save-column-arrangement/${sellerId}/${tableKey}`,
          { columnOrder: order }
        );
      } catch (error) {
        console.error("Error saving column order:", error);
      }
    },
    [baseurl, sellerId, tableKey]
  );

  useEffect(() => {
    const fetchColumnData = async () => {
      if (initialLoadComplete.current) return;

      try {
        setTableState((prev) => ({ ...prev, isLoading: false }));

        const localWidths = JSON.parse(
          localStorage.getItem(`columnWidths_${tableKey}`)
        );
        const localOrder = JSON.parse(
          localStorage.getItem(`columnOrder_${tableKey}`)
        );

        if (localWidths && Array.isArray(localWidths)) {
          const clampedLocal = localWidths.map((w) =>
            Math.max(Number(w), MIN_COLUMN_WIDTH)
          );
          setTableState((prev) => ({
            ...prev,
            columnWidths: clampedLocal,
          }));
        }

        if (localOrder && Array.isArray(localOrder)) {
          setTableState((prev) => ({
            ...prev,
            columnOrder: localOrder,
          }));
        }

        if (sellerId && tableKey && baseurl) {
          const [widthsResponse, orderResponse] = await Promise.allSettled([
            axios.get(
              `${baseurl}columnWidth/get-column-widths/${sellerId}/${tableKey}`,
              { timeout: 5000 }
            ),
            axios.get(
              `${baseurl}columnArrangement/get-column-arrangement/${sellerId}/${tableKey}`,
              { timeout: 5000 }
            ),
          ]);

          if (
            widthsResponse.status === "fulfilled" &&
            widthsResponse.value.data.data?.columnWidths
          ) {
            const apiWidthsRaw = widthsResponse.value.data.data.columnWidths;
            const apiWidths = apiWidthsRaw.map((w) =>
              Math.max(Number(w), MIN_COLUMN_WIDTH)
            );
            setTableState((prev) => ({
              ...prev,
              columnWidths: apiWidths,
            }));
            localStorage.setItem(
              `columnWidths_${tableKey}`,
              JSON.stringify(apiWidths)
            );
          }

          if (
            orderResponse.status === "fulfilled" &&
            orderResponse.value.data.data?.columnOrder
          ) {
            const apiOrder = orderResponse.value.data.data.columnOrder;
            setTableState((prev) => ({
              ...prev,
              columnOrder: apiOrder,
            }));
            localStorage.setItem(
              `columnOrder_${tableKey}`,
              JSON.stringify(apiOrder)
            );
          }
        }
      } catch (error) {
        console.error("Error fetching column data:", error);
        setTableState((prev) => ({ ...prev, isLoading: false }));
      }

      initialLoadComplete.current = true;
    };

    fetchColumnData();
  }, [baseurl, sellerId, tableKey]);

  const handleResize = useCallback(
    (originalIndex, newWidth) => {
      setTableState((prev) => {
        if (!prev.columnWidths || !prev.columnOrder) return prev;

        const updatedWidths = [...prev.columnWidths];
        const positionIndex = prev.columnOrder.indexOf(originalIndex);

        if (positionIndex === -1 || positionIndex >= updatedWidths.length) {
          console.warn(`Invalid column index for resize: ${originalIndex}`);
          return prev;
        }

        const clampedWidth = Math.max(
          Number(newWidth) || MIN_COLUMN_WIDTH,
          MIN_COLUMN_WIDTH
        );
        updatedWidths[positionIndex] = clampedWidth;

        // Save to localStorage immediately for responsive UI
        if (tableKey) {
          localStorage.setItem(
            `columnWidths_${tableKey}`,
            JSON.stringify(updatedWidths)
          );
        }

        // Debounce API save
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        if (tableKey && saveColumnWidths) {
          debounceTimeout.current = setTimeout(() => {
            saveColumnWidths(updatedWidths);
          }, 500);
        }

        return { ...prev, columnWidths: updatedWidths };
      });
    },
    [tableKey, saveColumnWidths]
  );

  const handleColumnReorder = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;

      setTableState((prev) => {
        if (!prev.columnOrder || !prev.columnWidths) return prev;

        const allColumns = extraColumn ? [...columns, extraColumn] : columns;
        const totalLength = allColumns.length;

        if (
          fromIndex < 0 ||
          fromIndex >= totalLength ||
          toIndex < 0 ||
          toIndex >= totalLength
        ) {
          console.warn(
            `Invalid column reorder indices: ${fromIndex} -> ${toIndex}`
          );
          return prev;
        }

        const newOrder = [...prev.columnOrder];
        const newWidths = [...prev.columnWidths];

        const actualFromIndex = newOrder.indexOf(fromIndex);
        const actualToIndex = newOrder.indexOf(toIndex);

        if (actualFromIndex === -1 || actualToIndex === -1) {
          console.warn(`Column indices not found in order array`);
          return prev;
        }

        // Perform the reorder
        const [movedOrderItem] = newOrder.splice(actualFromIndex, 1);
        const [movedWidthItem] = newWidths.splice(actualFromIndex, 1);

        newOrder.splice(actualToIndex, 0, movedOrderItem);
        newWidths.splice(actualToIndex, 0, movedWidthItem);

        // Validate the new order
        const isValidOrder =
          newOrder.every((index) => index >= 0 && index < totalLength) &&
          newOrder.length === totalLength;

        if (!isValidOrder) {
          console.warn(`Invalid column order after reorder:`, newOrder);
          return prev;
        }

        // Save to localStorage immediately
        if (tableKey) {
          localStorage.setItem(
            `columnOrder_${tableKey}`,
            JSON.stringify(newOrder)
          );
          localStorage.setItem(
            `columnWidths_${tableKey}`,
            JSON.stringify(newWidths)
          );
        }

        // Debounce API save
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        if (tableKey && saveColumnOrder && saveColumnWidths) {
          debounceTimeout.current = setTimeout(() => {
            saveColumnOrder(newOrder);
            saveColumnWidths(newWidths);
          }, 500);
        }

        return {
          ...prev,
          columnOrder: newOrder,
          columnWidths: newWidths,
          draggedColumn: null,
          dragOverColumn: null,
        };
      });
    },
    [columns, extraColumn, tableKey, saveColumnOrder, saveColumnWidths]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );
  const [activeDragColId, setActiveDragColId] = useState(null);

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over) return;

      let targetColumnIndex = null;
      if (typeof over.id === "number") {
        targetColumnIndex = over.id;
      }
      const overData = over?.data?.current;
      if (
        targetColumnIndex == null &&
        overData &&
        overData.targetColId != null
      ) {
        targetColumnIndex = overData.targetColId;
      }

      if (targetColumnIndex == null || active.id === targetColumnIndex) {
        setActiveDragColId(null);
        return;
      }

      handleColumnReorder(active.id, targetColumnIndex);
      setActiveDragColId(null);
    },
    [handleColumnReorder]
  );
  const onDragStart = useCallback((event) => {
    setActiveDragColId(event.active?.id ?? null);
  }, []);

  const getCurrentWidth = useCallback(
    (originalIndex) => {
      if (!tableState.columnOrder || !tableState.columnWidths) {
        return DEFAULT_COLUMN_WIDTH;
      }

      const positionIndex = tableState.columnOrder.indexOf(originalIndex);
      if (
        positionIndex === -1 ||
        positionIndex >= tableState.columnWidths.length
      ) {
        return DEFAULT_COLUMN_WIDTH;
      }

      const width = tableState.columnWidths[positionIndex];
      return Math.max(Number(width) || DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH);
    },
    [tableState.columnOrder, tableState.columnWidths]
  );

  const SortableHeaderCell = ({ id, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
    };

    const currentWidth = getCurrentWidth(id);

    return (
      <TableHead
        ref={setNodeRef}
        style={{
          width: `${currentWidth}px`,
          boxSizing: "border-box",
          ...style,
        }}
        className="tw-px-0 tw-relative"
        onMouseEnter={() =>
          setTableState((prev) => ({ ...prev, hoveredColumn: id }))
        }
        onMouseLeave={() =>
          setTableState((prev) => ({ ...prev, hoveredColumn: null }))
        }
      >
        {/* Draggable area covering entire header */}
        <div
          className={`tw-w-full tw-h-full tw-flex tw-items-center tw-px-4 tw-transition-colors tw-duration-200 ${
            isDragging
              ? "tw-bg-blue-100 tw-cursor-grabbing"
              : "tw-cursor-grab hover:tw-bg-gray-50"
          }`}
          {...attributes}
          {...listeners}
          title="Drag to reorder column"
        >
          <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-items-center tw-gap-2">
            <span className="tw-flex-1">{children}</span>
          </div>
        </div>

        {/* Resize handle - always visible but more subtle when not hovered */}
        <div
          className={`tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-4 tw-cursor-col-resize tw-z-10 tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-duration-200 tw-mr-4 ${
            tableState.hoveredColumn === id ? "tw-opacity-100" : "tw-opacity-0"
          } hover:tw-opacity-100`}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startWidth = getCurrentWidth(id);

            // Add visual feedback during resize
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";

            const doDrag = (dragEvent) => {
              dragEvent.preventDefault();
              const deltaX = dragEvent.clientX - startX;
              const newWidth = Math.max(startWidth + deltaX, MIN_COLUMN_WIDTH);
              handleResize(id, newWidth);
            };

            const stopDrag = () => {
              document.removeEventListener("mousemove", doDrag);
              document.removeEventListener("mouseup", stopDrag);
              // Restore cursor and selection
              document.body.style.cursor = "";
              document.body.style.userSelect = "";
            };

            document.addEventListener("mousemove", doDrag);
            document.addEventListener("mouseup", stopDrag);
          }}
          onClick={(e) => e.stopPropagation()}
          title="Drag to resize column"
        >
          <FaArrowsAltH className="tw-text-gray-600 tw-text-sm" />
        </div>
      </TableHead>
    );
  };

  const renderHeader = useCallback(
    (col, index) => (
      <span
        style={{
          cursor: col.sortable ? "pointer" : "inherit",
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (col.sortable) {
            setSortState((prev) => {
              const newDirection =
                prev.columnIndex === index && prev.direction === "asc"
                  ? "desc"
                  : "asc";
              return { columnIndex: index, direction: newDirection };
            });
            setCurrentPage(1);
          }
        }}
      >
        {col.name}
        {col.sortable && tableState.hoveredColumn === index && (
          <span style={{ marginLeft: 8, fontSize: 12 }}>
            {sortState.columnIndex === index && sortState.direction === "desc"
              ? "▼"
              : "▲"}
          </span>
        )}
      </span>
    ),
    [sortState.columnIndex, sortState.direction, tableState.hoveredColumn]
  );

  const resizedColumns = useMemo(() => {
    if (!Array.isArray(columns) || columns.length === 0) {
      return [];
    }

    if (!tableState.columnOrder || !tableState.columnWidths) {
      return [];
    }

    const allColumns = extraColumn ? [...columns, extraColumn] : columns;
    const totalLength = allColumns.length;

    // Ensure column order is valid
    const validColumnOrder = tableState.columnOrder.filter(
      (originalIndex) => originalIndex >= 0 && originalIndex < totalLength
    );

    // Add any missing columns to the end
    const missingIndices = [];
    for (let i = 0; i < totalLength; i++) {
      if (!validColumnOrder.includes(i)) {
        missingIndices.push(i);
      }
    }
    const completeColumnOrder = [...validColumnOrder, ...missingIndices];

    const orderedColumns = completeColumnOrder
      .filter((originalIndex) => {
        return (
          originalIndex < allColumns.length &&
          allColumns[originalIndex] !== undefined &&
          allColumns[originalIndex] !== null
        );
      })
      .map((originalIndex) => {
        const column = allColumns[originalIndex];
        const width = getCurrentWidth(originalIndex);
        const isExtraColumn = extraColumn && originalIndex === columns.length;

        return {
          ...column,
          name: renderHeader(column, originalIndex),
          width: `${width}px`,
          style: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...(column.style || {}),
            ...(isExtraColumn && {
              backgroundColor: "#f8f9fa",
              borderLeft: "2px solid #dee2e6",
            }),
          },
          __originalIndex: originalIndex,
        };
      });

    return orderedColumns;
  }, [
    columns,
    extraColumn,
    tableState.columnOrder,
    tableState.columnWidths,
    getCurrentWidth,
    renderHeader,
  ]);

  const BodyCellDroppable = ({ rowIndex, targetColId, children }) => {
    const { setNodeRef } = useDroppable({
      id: `col-body-${targetColId}-row-${rowIndex}`,
      data: { targetColId },
    });
    return (
      <div ref={setNodeRef} style={{ width: "100%", height: "100%" }}>
        {children}
      </div>
    );
  };

  useEffect(() => {
    if (!columns || columns.length === 0) return;
    const allColumns = extraColumn ? [...columns, extraColumn] : columns;
    const defaultIndex = allColumns.findIndex((c) => c.defaultSortField);
    if (defaultIndex !== -1) {
      setSortState({ columnIndex: defaultIndex, direction: "asc" });
    }
  }, [columns, extraColumn]);

  const getCellContent = useCallback((col, row) => {
    if (typeof col.cell === "function") return col.cell(row);
    if (typeof col.format === "function") return col.format(row);
    if (typeof col.selector === "function") return col.selector(row);
    if (typeof col.selector === "string") return row?.[col.selector];
    return "";
  }, []);

  const sortedData = useMemo(() => {
    if (
      sortState.columnIndex === null ||
      sortState.columnIndex < 0 ||
      !Array.isArray(data)
    )
      return Array.isArray(data) ? data : [];

    const allColumns = extraColumn ? [...columns, extraColumn] : columns;
    const colIndexInAll = sortState.columnIndex;
    const col = allColumns[colIndexInAll];
    if (!col || (!col.selector && !col.cell && !col.format)) return data;

    const copy = [...data];
    const directionFactor = sortState.direction === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      const aVal = getCellContent(col, a);
      const bVal = getCellContent(col, b);

      const aComp =
        typeof aVal === "object" ? String(aVal?.props?.children ?? "") : aVal;
      const bComp =
        typeof bVal === "object" ? String(bVal?.props?.children ?? "") : bVal;

      if (aComp == null && bComp == null) return 0;
      if (aComp == null) return -1 * directionFactor;
      if (bComp == null) return 1 * directionFactor;

      if (typeof aComp === "number" && typeof bComp === "number") {
        return (aComp - bComp) * directionFactor;
      }
      return String(aComp).localeCompare(String(bComp)) * directionFactor;
    });
    return copy;
  }, [data, columns, extraColumn, sortState, getCellContent]);

  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const total = sortedData.length;
    const start = (currentPage - 1) * rowsPerPage;
    const end = Math.min(start + rowsPerPage, total);
    return sortedData.slice(start, end);
  }, [sortedData, pagination, currentPage, rowsPerPage]);

  // Loading state
  if (tableState.isLoading && (!data || data.length === 0)) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64 tw-bg-white tw-rounded-md tw-border tw-border-gray-200">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-500"></div>
        <span className="tw-ml-3 tw-text-gray-600">Loading table data...</span>
      </div>
    );
  }

  // Error states
  if (!Array.isArray(columns) || columns.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-32 tw-bg-gray-50 tw-rounded-md tw-border tw-border-gray-200">
        <div className="tw-text-center">
          <div className="tw-text-gray-400 tw-text-lg mb-2">⚠️</div>
          <p className="tw-text-gray-600 tw-text-sm">
            No columns configured for this table
          </p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64 tw-bg-white tw-rounded-md tw-border tw-border-gray-200">
        <div className="tw-text-center">
          <div className="tw-text-gray-400 tw-text-4xl tw-mb-4">📊</div>
          <p className="tw-text-gray-600 tw-text-lg tw-font-medium tw-mb-2">
            No data available
          </p>
          <p className="tw-text-gray-500 tw-text-sm">
            Data will appear here when available
          </p>
        </div>
      </div>
    );
  }

  const columnsToRender = resizedColumns.length > 0 ? resizedColumns : columns;

  return (
    <div ref={tableRef} className="all-table-container">
      <div className="tw-w-full tw-overflow-hidden tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={columnsToRender.map((c) => c.__originalIndex)}
            strategy={horizontalListSortingStrategy}
          >
            <Table
              className="tw-min-w-full"
              style={{
                tableLayout: forceFixedLayout ? "fixed" : "auto",
                width: "100%",
              }}
            >
              <TableHeader>
                <TableRow>
                  {columnsToRender.map((col) => (
                    <SortableHeaderCell
                      key={`head_${col.__originalIndex}`}
                      id={col.__originalIndex}
                      width={col.width}
                    >
                      {col.name}
                    </SortableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((row, rowIndex) => (
                  <TableRow key={`row_${rowIndex}`}>
                    {columnsToRender.map((col, colIndex) => (
                      <TableCell
                        key={`cell_${rowIndex}_${colIndex}`}
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          ...col.style,
                        }}
                        className="tw-px-4 tw-py-2"
                      >
                        <BodyCellDroppable
                          rowIndex={rowIndex}
                          targetColId={col.__originalIndex}
                        >
                          <div
                            className="tw-truncate tw-w-full"
                            title={String(getCellContent(col, row))}
                          >
                            {getCellContent(col, row)}
                          </div>
                        </BodyCellDroppable>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SortableContext>
          <DragOverlay>
            {(() => {
              if (activeDragColId == null) return null;
              const col = columnsToRender.find(
                (c) => c.__originalIndex === activeDragColId
              );
              if (!col) return null;
              return (
                <div className="tw-bg-white tw-shadow-lg tw-rounded-sm">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          style={{ width: col.width, minWidth: col.width }}
                        >
                          {col.name}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedData.map((row, rowIndex) => (
                        <TableRow key={`overlay_row_${rowIndex}`}>
                          <TableCell
                            style={{
                              width: col.width,
                              minWidth: col.width,
                            }}
                          >
                            {getCellContent(col, row)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}
          </DragOverlay>
        </DndContext>

        {pagination && (
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-sm:tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-gap-3 tw-sm:tw-gap-0">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-sm tw-text-gray-700 tw-font-medium">
                Rows per page:
              </span>
              <select
                className="tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm tw-bg-white tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:border-blue-500"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 15, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="tw-flex tw-items-center tw-gap-4">
              <span className="tw-text-sm tw-text-gray-600 tw-font-medium">
                {(() => {
                  const total = sortedData.length;
                  if (total === 0) return "No items";
                  const start = (currentPage - 1) * rowsPerPage + 1;
                  const end = Math.min(currentPage * rowsPerPage, total);
                  return `${start}-${end} of ${total.toLocaleString()}`;
                })()}
              </span>

              <div className="tw-flex tw-items-center tw-gap-1">
                <button
                  className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  «
                </button>
                <button
                  className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  ‹
                </button>

                <span className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200 tw-rounded-md tw-min-w-[2.5rem] tw-text-center">
                  {currentPage}
                </span>

                <button
                  className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                  onClick={() => {
                    const totalPages = Math.max(
                      1,
                      Math.ceil(sortedData.length / rowsPerPage)
                    );
                    setCurrentPage((p) => Math.min(totalPages, p + 1));
                  }}
                  disabled={
                    currentPage >= Math.ceil(sortedData.length / rowsPerPage)
                  }
                  title="Next page"
                >
                  ›
                </button>
                <button
                  className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                  onClick={() => {
                    const totalPages = Math.max(
                      1,
                      Math.ceil(sortedData.length / rowsPerPage)
                    );
                    setCurrentPage(totalPages);
                  }}
                  disabled={
                    currentPage >= Math.ceil(sortedData.length / rowsPerPage)
                  }
                  title="Last page"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
